import React from 'react';
import { IconButton, Drawer, Spinner } from '@parsina/framework';
import { ArrowRight, Menu as MenuIcon } from 'lucide-react';

import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { PracticeBadge, AiDisclaimer } from '../../components/common/PracticeNotice.jsx';
import ErrorState from '../../components/common/ErrorState.jsx';
import ThreadSidebar from '../../components/chat/ThreadSidebar.jsx';
import MessageList from '../../components/chat/MessageList.jsx';
import SuggestedPrompts from '../../components/chat/SuggestedPrompts.jsx';
import FollowUps from '../../components/chat/FollowUps.jsx';
import Composer from '../../components/chat/Composer.jsx';
import { threadTitle } from '../../lib/conversation.js';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { canAfford } from '../../components/common/ParsehCost.jsx';

// The phone chat. core.md §9.1.
//
// The same conversation, three chrome changes:
//
//   1. the 260px rail becomes a DRAWER behind a hamburger. The kit's `Drawer`
//      docks at the inline-END, which under `dir="rtl"` is the left edge — the
//      side §9.1 names — and it flips by itself for the English locale rather
//      than by a direction-conditional style here;
//   2. the composer is DOCKED, and it respects `--app-bottom-nav` so it is
//      never underneath the shell's bar. It sits above it, not behind it;
//   3. the bottom nav is hidden on this route entirely (`showsBottomNav` in
//      constants/nav.js): a full-height workspace with its own back button.
//
// The whole surface is `position: relative` because the kit's Drawer positions
// itself ABSOLUTELY — inside a static ancestor it would escape to the viewport
// and cover the shell.

export default function ChatMobile({
  threadId, name, caseRecord, caseError, reloadCase,
  balance, chat, send, openThread, startNew, rename, remove,
  focusSignal, offline, onBack, onTopUp, onSwitchPatient, onOpenVisits,
}) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const title = threadTitle(chat.thread, chat.pendingTitle || t('chat.thread.untitled'));
  const empty = !chat.loading && chat.messages.length === 0;
  // A KNOWN balance that cannot pay for one question disables the send and
  // shows the top-up card BEFORE the spend (core.md §3.5, §9.2). An unknown
  // balance allows it — the server is the real gate, and it refuses too.
  const outOfCredits = !canAfford(balance, useCreditPrices().costOf('ASK'));

  const openAndClose = (id) => { setDrawerOpen(false); openThread(id); };

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        height: '100%', minHeight: 0, overflow: 'hidden',
        background: C.bg,
      }}
    >
      <header
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 10px', borderBottom: `1px solid ${C.border}`,
          background: C.card, flexShrink: 0,
        }}
      >
        <IconButton
          size={38}
          variant="ghost"
          icon={<MenuIcon size={18} aria-hidden />}
          label={t('chat.sidebar.title')}
          onClick={() => setDrawerOpen(true)}
          data-testid="sidebar-toggle"
        />
        <IconButton
          size={38}
          variant="ghost"
          icon={<ArrowRight size={18} aria-hidden style={{ transform: 'scaleX(var(--dir-flip, 1))' }} />}
          label={t('chat.header.back')}
          onClick={onBack}
          data-testid="chat-back"
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span
              data-testid="chat-patient-name"
              style={{
                fontSize: 14, fontWeight: 800, color: C.textPrimary,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {name}
            </span>
            <PracticeBadge short />
          </div>
          {chat.thread || chat.pendingTitle ? (
            <div
              data-testid="chat-title"
              style={{
                fontSize: 11.5, color: C.textMuted,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {title}
            </div>
          ) : null}
        </div>
      </header>

      <AiDisclaimer style={{ margin: '8px 10px 0' }} />

      {chat.error ? (
        <ErrorState error={chat.error} title={t('chat.error.title')} onRetry={chat.reloadThreads} fill />
      ) : caseError ? (
        <ErrorState error={caseError} onRetry={reloadCase} fill />
      ) : chat.loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner label={t('common.labels.loading')} />
        </div>
      ) : empty ? (
        <SuggestedPrompts name={name} caseRecord={caseRecord} onPick={send} />
      ) : (
        <MessageList
          messages={chat.messages}
          reveal={chat.reveal}
          confidence={chat.answer?.confidence}
          sending={chat.sending}
          hasOlder={chat.hasOlder}
          loadingOlder={chat.loadingOlder}
          onLoadOlder={chat.loadOlder}
          onRetry={chat.retry}
          onRegenerate={chat.regenerate}
        />
      )}

      <FollowUps items={chat.answer?.followUps} onPick={send} disabled={chat.sending} />

      {/* Docked, and never under the shell's bar: the composer sits ABOVE
          `--app-bottom-nav`, which is 0 on a route that hides the bar. */}
      <div style={{ flexShrink: 0, paddingBottom: 'var(--app-bottom-nav, 0px)' }}>
        <Composer
          value={chat.draft}
          onChange={chat.setDraft}
          onSend={send}
          onStop={chat.stopReveal}
          sending={chat.sending}
          revealing={chat.revealing}
          offline={offline}
          outOfCredits={outOfCredits}
          notice={chat.notice}
          onTopUp={onTopUp}
          focusSignal={focusSignal}
        />
      </div>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} width={280}>
        <ThreadSidebar
          threads={chat.threads}
          loading={chat.threadsLoading}
          activeThreadId={threadId}
          balance={balance}
          onNew={() => { setDrawerOpen(false); startNew(); }}
          onOpen={openAndClose}
          onRename={rename}
          onDelete={remove}
          onSwitchPatient={onSwitchPatient}
          onOpenVisits={onOpenVisits}
        />
      </Drawer>
    </div>
  );
}
